import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Stack
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axiosInstance from '../../api/axiosConfig';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/dashboard-stats');
      setStats(response.data.data || response.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data');
      setStats({
        totalStudents: 245,
        activeStudents: 189,
        totalResults: 523,
        passPercentage: 87,
        failedStudents: 32,
        totalCourses: 12,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
        transition: '0.3s',
        '&:hover': { transform: 'translateY(-6px)' }
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.6rem', md: '2rem' },
                color
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              background: `${color}15`,
              p: 1.5,
              borderRadius: 2
            }}
          >
            <Icon sx={{ color, fontSize: 30 }} />
          </Box>
        </Stack>

        {trend && (
          <Stack direction="row" alignItems="center" mt={1}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ color: '#f44336', fontSize: 18 }} />
            )}
            <Typography
              variant="caption"
              sx={{ ml: 0.5, color: trend > 0 ? '#4caf50' : '#f44336' }}
            >
              {Math.abs(trend)}% this month
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700}>
          Dashboard Overview
        </Typography>
        <Typography color="text.secondary">
          Welcome to Kavya Institute Admin Portal
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stat Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Students" value={stats.totalStudents} icon={PeopleIcon} color="#1976d2" trend={12} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Students" value={stats.activeStudents} icon={SchoolIcon} color="#4caf50" trend={8} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Results" value={stats.totalResults} icon={AssignmentIcon} color="#ff9800" trend={15} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Courses" value={stats.totalCourses} icon={SchoolIcon} color="#9c27b0" trend={3} />
        </Grid>
      </Grid>

      {/* Performance Section */}
      <Grid container spacing={3} mt={1}>
        {/* Pass Percentage */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 6px 20px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Pass Percentage
              </Typography>
              <Stack direction={isMobile ? 'column' : 'row'} alignItems="center" spacing={3}>
                <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                  <svg width="120" height="120">
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#eee" strokeWidth="8" />
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 54 * stats.passPercentage / 100} ${2 * Math.PI * 54}`}
                      transform="rotate(-90 60 60)"
                      strokeLinecap="round"
                    />
                    <text x="60" y="68" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#4caf50">
                      {stats.passPercentage}%
                    </text>
                  </svg>
                </Box>
                <Typography color="text.secondary">
                  Students who successfully passed the exam this semester.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Failed Students */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 6px 20px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Failed Students
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" fontWeight={700}>
                      {stats.failedStudents}
                    </Typography>
                    <Typography variant="caption">Total Failed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {((stats.failedStudents / stats.totalStudents) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption">Failure Rate</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Summary */}
      <Card sx={{ mt: 4, borderRadius: 3, boxShadow: '0 6px 20px rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Quick Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Enrollment Rate</Typography>
              <Typography variant="h5" fontWeight={700}>
                {((stats.activeStudents / stats.totalStudents) * 100).toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Students Per Course</Typography>
              <Typography variant="h5" fontWeight={700}>
                {(stats.totalStudents / stats.totalCourses).toFixed(0)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Results Published</Typography>
              <Typography variant="h5" fontWeight={700}>
                {((stats.totalResults / stats.totalStudents) * 100).toFixed(1)}%
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
